'use client';

import { ImageViewer } from '@andritzot/metris-web-ui/data-display/image';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Skeleton } from '@andritzot/metris-web-ui/feedback/skeleton';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface CarouselImage {
  src: string;
  caption?: string;
}

interface OverviewImageCarouselProps {
  images: CarouselImage[];
  loading?: boolean;
  height?: string;
}

const sxTrack = {
  flex: 1,
  display: 'flex',
  gap: '16px',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
};

const DRAG_THRESHOLD_PX = 5;

function useDragToScroll(trackRef: RefObject<HTMLDivElement | null>) {
  const drag = useRef({ active: false, startX: 0, startScrollLeft: 0, moved: false });

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Touch and pen already scroll the track natively; dragging only needs wiring for the mouse.
    if (event.pointerType !== 'mouse') return;
    const track = trackRef.current;
    if (!track) return;
    drag.current = { active: true, startX: event.clientX, startScrollLeft: track.scrollLeft, moved: false };
    // Mandatory snapping fights direct scrollLeft writes mid-drag; re-enabled on release.
    track.style.scrollSnapType = 'none';
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!drag.current.active || !track) return;
    const deltaX = event.clientX - drag.current.startX;
    if (Math.abs(deltaX) > DRAG_THRESHOLD_PX) drag.current.moved = true;
    track.scrollLeft = drag.current.startScrollLeft - deltaX;
  };

  const endDrag = () => {
    drag.current.active = false;
    const track = trackRef.current;
    if (track) track.style.scrollSnapType = '';
  };

  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    // A drag release lands on an image; swallow that click so it doesn't open the preview.
    if (!drag.current.moved) return;
    drag.current.moved = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const onDragStart = (event: React.DragEvent<HTMLDivElement>) => event.preventDefault();

  return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerLeave: endDrag, onClickCapture, onDragStart };
}

export const OverviewImageCarousel = ({ images, loading = false, height = '100px' }: OverviewImageCarouselProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragHandlers = useDragToScroll(trackRef);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollBack(track.scrollLeft > 0);
    setCanScrollForward(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(track);
    Array.from(track.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [updateScrollState, images.length]);

  const scrollByViewport = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
  };

  if (loading) {
    return <Skeleton width="100%" height={height} />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <IconButton
        onClick={() => scrollByViewport(-1)}
        disabled={!canScrollBack}
        aria-label="Previous images"
        size="small"
      >
        <ChevronLeft />
      </IconButton>

      <Box ref={trackRef} onScroll={updateScrollState} sx={sxTrack} {...dragHandlers}>
        {images.map((image) => (
          <Box key={image.src} sx={{ flexShrink: 0, scrollSnapAlign: 'start', maxWidth: '100%' }}>
            <ImageViewer src={image.src} alt={image.caption || ''} height={height} preview />
            {image.caption && (
              <Typography variant="caption" component="p" sx={{ textAlign: 'center', mt: 0.5 }}>
                {image.caption}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <IconButton
        onClick={() => scrollByViewport(1)}
        disabled={!canScrollForward}
        aria-label="Next images"
        size="small"
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );
};
